// ═══════════════════════════════════════════════════════════════════════════════
// Admin idle session warning (28m) + force logout (30m)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { tryAdminRefreshToken } from '../../lib/adminApi';
import { adminPortalPath } from '../../lib/adminPortalPaths';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';

const IDLE_WARN_MS = 28 * 60 * 1000;
const IDLE_FORCE_MS = 30 * 60 * 1000;
const CHECK_INTERVAL_MS = 5000;
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const;

export const AdminIdleSessionGuard = () => {
  const { isAuthenticated, admin, logout } = useAdminAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [warningOpen, setWarningOpen] = useState(false);
  const [extending, setExtending] = useState(false);
  const lastActivityAtRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const forcingLogoutRef = useRef(false);

  const idleEnabled = isAuthenticated && admin?.breakGlassSession !== true;

  const handleForceLogout = useCallback(async () => {
    if (forcingLogoutRef.current) {
      return;
    }
    forcingLogoutRef.current = true;
    setWarningOpen(false);
    try {
      await logout();
    } catch {
      // ignore
    }
    navigate(adminPortalPath('login'), { replace: true });
    forcingLogoutRef.current = false;
  }, [logout, navigate]);

  const handleExtendSession = useCallback(async () => {
    setExtending(true);
    try {
      await tryAdminRefreshToken();
      lastActivityAtRef.current = Date.now();
      warningShownRef.current = false;
      setWarningOpen(false);
    } finally {
      setExtending(false);
    }
  }, []);

  useEffect(() => {
    if (!idleEnabled) {
      setWarningOpen(false);
      warningShownRef.current = false;
      lastActivityAtRef.current = Date.now();
      return undefined;
    }

    const markActivity = () => {
      if (warningShownRef.current) {
        return;
      }
      lastActivityAtRef.current = Date.now();
    };

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, markActivity, { passive: true });
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !warningShownRef.current) {
        lastActivityAtRef.current = Date.now();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const intervalId = window.setInterval(() => {
      const idleMs = Date.now() - lastActivityAtRef.current;
      if (idleMs >= IDLE_FORCE_MS) {
        void handleForceLogout();
        return;
      }
      if (idleMs >= IDLE_WARN_MS && !warningShownRef.current) {
        warningShownRef.current = true;
        setWarningOpen(true);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, markActivity);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(intervalId);
    };
  }, [idleEnabled, handleForceLogout]);

  if (!idleEnabled) {
    return null;
  }

  return (
    <ConfirmDialog
      open={warningOpen}
      title={t('admin.idleWarningTitle')}
      description={t('admin.idleWarningDescription')}
      confirmLabel={t('admin.idleExtendSession')}
      cancelLabel={t('admin.idleLogoutNow')}
      onConfirm={() => {
        void handleExtendSession();
      }}
      onCancel={() => {
        void handleForceLogout();
      }}
      confirmDisabled={extending}
      cancelDisabled={extending}
      backdropZClass="z-[120]"
    />
  );
};
